import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetLocationsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/inputs/get-locations-paginated.model';
import { GatewayLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/outputs/gateway-location.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { NetworkFilterStateController } from '@app/feature/network/network-filter-state/network-filter-state.controller';

import { LocationListAddressColumn } from './location-list-address.column';
import { LocationListCityColumn } from './location-list-city.column';
import { LocationListIdColumn } from './location-list-id.column';
import { LocationListNameColumn } from './location-list-name.column';
import { LocationListNetworkColumn } from './location-list-network.column';
import { LocationListOperatorColumn } from './location-list-operator.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const LOCATION_LIST_SORT_FIELD: NonNullable<
  GetLocationsPaginatedModel['sortField']
> = 'createdAt';

export const LOCATION_LIST_SORT_ORDER: NonNullable<
  GetLocationsPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetLocationsPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const LOCATION_LIST_FILTER: ResourceListFilter = {
  networkId: {
    componentType: NetworkFilterStateController,
  },
} as const;

export const LOCATION_LIST_FILTER_NAME_LIST = Object.keys(
  LOCATION_LIST_FILTER,
) as Array<keyof typeof LOCATION_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const locationListColumnHelper =
  createColumnHelper<GatewayLocationModel>();

type ResourceColumnType = ColumnDef<GatewayLocationModel, any>[];

export const LOCATION_LIST_COLUMN_LIST: ResourceColumnType = [
  locationListColumnHelper.accessor('id', LocationListIdColumn),
  locationListColumnHelper.accessor('name', LocationListNameColumn),
  locationListColumnHelper.accessor('address', LocationListAddressColumn),
  locationListColumnHelper.accessor('city', LocationListCityColumn),
  locationListColumnHelper.accessor('operator', LocationListOperatorColumn),
  locationListColumnHelper.accessor('network', LocationListNetworkColumn),
];

export const LOCATION_LIST_COLUMN_ID_LIST = LOCATION_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const LOCATION_LIST_COLUMN_ORDER = [...LOCATION_LIST_COLUMN_ID_LIST];

export const LOCATION_LIST_COLUMN_VISIBILITY =
  LOCATION_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {});

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface LocationListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const LOCATION_LIST_DEFAULT_OPTIONS: LocationListOptionsModel = {
  columnVisibility: LOCATION_LIST_COLUMN_VISIBILITY,
  columnOrder: LOCATION_LIST_COLUMN_ORDER,
};

export const LOCATION_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  id: 'delmonicos-shared-sdk.outputs-base.id',
  name: 'delmonicos-shared-sdk.outputs-location.name',
  address: 'delmonicos-shared-sdk.outputs-location.full-address',
  city: 'delmonicos-shared-sdk.outputs-location.city',
  operator: 'delmonicos-shared-sdk.outputs-location.operator',
  network: 'delmonicos-shared-sdk.outputs-location.network',
};
